/** Read a drag-and-drop DataTransfer into a flat list of files, preserving the
 *  relative path of each file inside any dropped folders.
 *
 *  Folder support relies on the (non-standard but universally implemented)
 *  `webkitGetAsEntry()` / FileSystemEntry API. The entries MUST be pulled out
 *  of `dataTransfer.items` synchronously — the DataTransferItemList is emptied
 *  as soon as the drop event handler returns, so we capture the entries first
 *  and only then traverse them asynchronously.
 *
 *  `relPath` is the path of the file relative to the drop, including any parent
 *  folders (e.g. "Contrats/2024/dpa.pdf"). A loose file dropped at the top
 *  level has `relPath` equal to its name (e.g. "note.md").
 */
export interface DroppedFile {
  file: File;
  relPath: string;
}

// Minimal typings for the FileSystem Entry API (not in the default DOM lib in
// a way we can rely on across versions). We only use what we need.
interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
}
interface FsFileEntry extends FsEntry {
  file(onSuccess: (file: File) => void, onError: (err: unknown) => void): void;
}
interface FsDirEntry extends FsEntry {
  createReader(): FsDirReader;
}
interface FsDirReader {
  readEntries(
    onSuccess: (entries: FsEntry[]) => void,
    onError: (err: unknown) => void,
  ): void;
}

export async function readDroppedEntries(
  dt: DataTransfer,
): Promise<DroppedFile[]> {
  const items = dt.items;
  // Capture entries synchronously before any await.
  const roots: FsEntry[] = [];
  let entryApiAvailable = false;
  if (items && items.length > 0) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const asEntry = (
        item as DataTransferItem & {
          webkitGetAsEntry?: () => FsEntry | null;
        }
      ).webkitGetAsEntry;
      if (typeof asEntry === "function") {
        entryApiAvailable = true;
        const entry = asEntry.call(item);
        if (entry) roots.push(entry);
      }
    }
  }

  // Fallback: no entry API → flat file list, relPath = name.
  if (!entryApiAvailable) {
    return Array.from(dt.files).map((file) => ({ file, relPath: file.name }));
  }

  const out: DroppedFile[] = [];
  for (const entry of roots) {
    await walkEntry(entry, "", out);
  }
  return out;
}

async function walkEntry(
  entry: FsEntry,
  parentPath: string,
  out: DroppedFile[],
): Promise<void> {
  const path = parentPath ? `${parentPath}/${entry.name}` : entry.name;
  if (entry.isFile) {
    const file = await getFile(entry as FsFileEntry);
    out.push({ file, relPath: path });
  } else if (entry.isDirectory) {
    const children = await readAllEntries((entry as FsDirEntry).createReader());
    for (const child of children) {
      await walkEntry(child, path, out);
    }
  }
}

function getFile(entry: FsFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/** `readEntries` returns directory contents in batches; it must be called
 *  repeatedly until it yields an empty array, otherwise large folders are
 *  silently truncated. */
async function readAllEntries(reader: FsDirReader): Promise<FsEntry[]> {
  const all: FsEntry[] = [];
  for (;;) {
    const batch = await new Promise<FsEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (batch.length === 0) break;
    all.push(...batch);
  }
  return all;
}
